import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Placement } from '../types';
import { clamp, storage } from '../utils';

export interface Size {
  width: number;
  height: number;
}

interface Options {
  placement: Placement;
  defaultSize: Size;
  minWidth: number;
  minHeight: number;
  enabled: boolean;
  persist: boolean;
  storageKey: string;
}

const VIEWPORT_MARGIN = 32;

export interface UseResizableResult {
  size: Size;
  isResizing: boolean;
  onHandlePointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  resetSize: () => void;
}

/**
 * Corner-drag resizing for a panel pinned to a bottom corner: the handle sits
 * on the opposite (top inner) corner, so dragging away from the anchor grows
 * the panel.
 */
export function useResizable(options: Options): UseResizableResult {
  const { placement, defaultSize, minWidth, minHeight, enabled, persist, storageKey } = options;
  const sizeKey = `${storageKey}:size`;

  /**
   * The size the user asked for. It is never overwritten by viewport limits,
   * so shrinking then re-enlarging the window restores the chosen size.
   */
  const [desired, setDesired] = useState<Size>(() => {
    if (persist && typeof window !== 'undefined') {
      const saved = storage.get<Size>(sizeKey);
      if (saved && Number.isFinite(saved.width) && Number.isFinite(saved.height)) return saved;
    }
    return defaultSize;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [viewport, setViewport] = useState(() =>
    typeof window === 'undefined'
      ? { width: 9999, height: 9999 }
      : { width: window.innerWidth, height: window.innerHeight },
  );

  const dragRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  const maxSize = useCallback(
    (): Size => ({
      width: Math.max(minWidth, viewport.width - VIEWPORT_MARGIN),
      height: Math.max(minHeight, viewport.height - VIEWPORT_MARGIN),
    }),
    [minWidth, minHeight, viewport],
  );

  const size = useMemo<Size>(() => {
    const bounds = maxSize();
    return {
      width: clamp(desired.width, minWidth, bounds.width),
      height: clamp(desired.height, minHeight, bounds.height),
    };
  }, [desired, maxSize, minWidth, minHeight]);

  const onHandlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragRef.current = { x: event.clientX, y: event.clientY, ...size };
      setIsResizing(true);
    },
    [enabled, size],
  );

  useEffect(() => {
    if (!isResizing) return;

    const onMove = (event: PointerEvent) => {
      const start = dragRef.current;
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      const bounds = maxSize();
      const widthDelta = placement === 'bottom-right' ? -dx : dx;

      setDesired({
        width: clamp(start.width + widthDelta, minWidth, bounds.width),
        height: clamp(start.height - dy, minHeight, bounds.height),
      });
    };

    const onUp = () => {
      dragRef.current = null;
      setIsResizing(false);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isResizing, maxSize, minHeight, minWidth, placement]);

  // Track the viewport so `size` re-clamps as the window changes.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (persist && !isResizing) storage.set(sizeKey, desired);
  }, [persist, sizeKey, desired, isResizing]);

  const resetSize = useCallback(() => {
    setDesired(defaultSize);
    if (persist) storage.remove(sizeKey);
  }, [defaultSize, persist, sizeKey]);

  return { size, isResizing, onHandlePointerDown, resetSize };
}
